import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  LayoutDashboard,
  FlaskConical,
  Building2,
  Users,
  HelpCircle,
  MessageSquare,
  Calendar,
  AlertTriangle,
  Plug,
  Shield,
  Settings,
  ScrollText,
  CreditCard,
  FileText,
  FolderArchive,
  Table2,
  Database,
  BookTemplate,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "@/stores/ui-store";
import { semanticSearch } from "@/api/client";
import type { Study, Site } from "@/api/types";

interface PaletteResult {
  type: "page" | "study" | "site" | "document";
  label: string;
  sublabel?: string;
  href: string;
  icon: LucideIcon;
}

const DOC_TYPE_ICONS: Record<string, LucideIcon> = {
  protocol: FileText,
  etmf: FolderArchive,
  crf_definition: Table2,
  edc_record: Database,
  ai_conversation: MessageSquare,
  audit_log: ScrollText,
  data_quality_alert: AlertTriangle,
  calendar_event: Calendar,
  public_template: BookTemplate,
};

const DOC_TYPE_ROUTES: Record<string, string> = {
  protocol: "/studies",
  etmf: "/studies",
  crf_definition: "/studies",
  edc_record: "/subjects",
  ai_conversation: "/chat",
  audit_log: "/audit",
  data_quality_alert: "/alerts",
  calendar_event: "/calendar",
  public_template: "/studies",
};

const PAGE_RESULTS: PaletteResult[] = [
  { type: "page", label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { type: "page", label: "Studies", href: "/studies", icon: FlaskConical },
  { type: "page", label: "Sites", href: "/sites", icon: Building2 },
  { type: "page", label: "Subjects", href: "/subjects", icon: Users },
  { type: "page", label: "Queries", href: "/queries", icon: HelpCircle },
  { type: "page", label: "AI Chat", href: "/chat", icon: MessageSquare },
  { type: "page", label: "Calendar", href: "/calendar", icon: Calendar },
  { type: "page", label: "Alerts", href: "/alerts", icon: AlertTriangle },
  { type: "page", label: "Integrations", href: "/integrations", icon: Plug },
  { type: "page", label: "Users & Roles", href: "/users", icon: Shield },
  { type: "page", label: "Settings", href: "/settings", icon: Settings },
  { type: "page", label: "Billing", href: "/billing", icon: CreditCard },
  { type: "page", label: "Audit Trail", href: "/audit", icon: ScrollText },
  { type: "page", label: "Search", href: "/search", icon: Search },
  { type: "page", label: "Organizations", href: "/admin/organizations", icon: Building2 },
];

export function CommandPalette() {
  const { commandPaletteOpen, closeCommandPalette } = useUIStore();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [semanticResults, setSemanticResults] = useState<PaletteResult[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Global keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        useUIStore.getState().openCommandPalette();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery("");
      setSelectedIndex(0);
      setSemanticResults([]);
      setSemanticLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  // Debounced semantic search
  useEffect(() => {
    if (query.trim().length < 3) {
      setSemanticResults([]);
      return;
    }

    setSemanticLoading(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await semanticSearch({ query: query.trim(), limit: 5 });
        setSemanticResults(
          res.results.map((r) => ({
            type: "document" as const,
            label: r.title || "Untitled",
            sublabel: r.sectionTitle ?? undefined,
            href: DOC_TYPE_ROUTES[r.documentType] ?? "/search",
            icon: DOC_TYPE_ICONS[r.documentType] ?? FileText,
          })),
        );
      } catch {
        setSemanticResults([]);
      } finally {
        setSemanticLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  const getResults = useCallback((): PaletteResult[] => {
    const q = query.toLowerCase().trim();
    if (!q) return PAGE_RESULTS.slice(0, 6);

    const results: PaletteResult[] = [];

    // Filter pages
    const pageMatches = PAGE_RESULTS.filter((p) =>
      p.label.toLowerCase().includes(q),
    );
    results.push(...pageMatches);

    // Search studies from cache
    const studiesCache = queryClient.getQueriesData<{ studies: Study[] }>({
      queryKey: ["studies"],
    });
    for (const [, data] of studiesCache) {
      if (!data?.studies) continue;
      for (const study of data.studies) {
        if (
          study.name.toLowerCase().includes(q) ||
          study.protocolNumber?.toLowerCase().includes(q)
        ) {
          results.push({
            type: "study",
            label: study.name,
            sublabel: study.protocolNumber ?? undefined,
            href: `/studies?studyId=${study.id}`,
            icon: FlaskConical,
          });
        }
      }
    }

    // Search sites from cache
    const sitesCache = queryClient.getQueriesData<{ sites: Site[] }>({
      queryKey: ["sites"],
    });
    for (const [, data] of sitesCache) {
      if (!data?.sites) continue;
      for (const site of data.sites) {
        if (
          site.name.toLowerCase().includes(q) ||
          site.siteNumber?.toLowerCase().includes(q)
        ) {
          results.push({
            type: "site",
            label: site.name,
            sublabel: site.siteNumber,
            href: `/sites?siteId=${site.id}`,
            icon: Building2,
          });
        }
      }
    }

    return results.slice(0, 10);
  }, [query, queryClient]);

  const cacheResults = getResults();
  const allResults = [...cacheResults, ...semanticResults];

  const handleSelect = useCallback(
    (result: PaletteResult) => {
      closeCommandPalette();
      navigate(result.href);
    },
    [closeCommandPalette, navigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeCommandPalette();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, allResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && allResults[selectedIndex]) {
      handleSelect(allResults[selectedIndex]);
    }
  };

  if (!commandPaletteOpen) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={closeCommandPalette}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, studies, sites, documents..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto p-2">
          {allResults.length === 0 && !semanticLoading ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              No results found
            </div>
          ) : (
            <>
              {cacheResults.map((result, i) => {
                const Icon = result.icon;
                return (
                  <button
                    key={`${result.type}-${result.href}`}
                    onClick={() => handleSelect(result)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                      i === selectedIndex
                        ? "bg-primary/10 text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{result.label}</span>
                      {result.sublabel && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {result.sublabel}
                        </span>
                      )}
                    </div>
                    <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                      {result.type}
                    </span>
                  </button>
                );
              })}

              {/* Documents divider + semantic results */}
              {(semanticResults.length > 0 || semanticLoading) && (
                <>
                  <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase text-muted-foreground">
                    <span>Documents</span>
                    {semanticLoading && (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    )}
                  </div>
                  {semanticResults.map((result, i) => {
                    const globalIndex = cacheResults.length + i;
                    const Icon = result.icon;
                    return (
                      <button
                        key={`doc-${i}-${result.label}`}
                        onClick={() => handleSelect(result)}
                        className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                          globalIndex === selectedIndex
                            ? "bg-primary/10 text-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <span className="font-medium">{result.label}</span>
                          {result.sublabel && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {result.sublabel}
                            </span>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                          {result.type}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
